import {createDraftSafeSelector, createEntityAdapter, createSlice, EntityId} from '@reduxjs/toolkit';
import { RootState } from "../index";
import { fetchPost, fetchPosts, updatePost, createPost, resetRand } from "../../actions/community/postsActions";
import { fetchReport, fetchReports } from "../../actions/community/reportsActions";

export interface PostInterface {
  url: string;

  id: number;
  app_id: number;
  category_id: number;
  category?: number | null
  kind_id?: number | null;
  target: 'content' | 'community';
  user_id?: number | null;
  user?: number | null;

  coins: number;
  purchase_type: 'free' | 'coins' | 'ads';

  title: string;
  description: string;
  search_text: string;

  thumbnail_url: string;
  file_url: string;

  likes_count: number;
  popular_count: number;
  comments_count: number;
  downloads_count: number;

  published_at: string;
  published: boolean;
  created_at: string;
  updated_at: string;
}

type SilentType = { [key: string]: { ids: number[], count: number } };

export interface initialInterface {
  error: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  count: number;

  silent: SilentType;

  editId: EntityId | null;
  editError: string | null;

  newParams: PostInterface | undefined;
  newError: string | null;
}

const initialState: initialInterface = {
  error: null,
  status: 'idle',
  count: 0,

  silent: {},

  editId: null,
  editError: null,

  newParams: undefined,
  newError: null
};

const adaptor = createEntityAdapter<PostInterface>();

const slice = createSlice({
  name: 'posts',
  initialState: adaptor.getInitialState(initialState),
  reducers: {
    setNewParams: (state, action) => {
      state.newParams = action.payload;
      state.newError  = initialState.newError;
    },
    setNewError: (state, action) => {
      state.newError = action.payload;
    },
    setEditId: (state, action) => {
      state.editId    = action.payload;
      state.editError = initialState.editError;
    },
    setEditError: (state, action) => {
      state.editError = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // #index
      .addCase(fetchPosts.pending, (state, _action) => {
        state.status = 'loading'
      })
      .addCase(fetchPosts.fulfilled, (state, action) => {
        if (action.payload.silentId) {
          state.silent[action.payload.silentId] = state.silent[action.payload.silentId] || { ids: [], count: 0 };

          state.silent[action.payload.silentId].ids = action.payload.ids;
          state.silent[action.payload.silentId].count = action.payload.count;
          state.entities = { ...state.entities, ...action.payload.posts };
        } else {
          state.ids = action.payload.ids;
          state.entities = action.payload.posts;
          state.count = action.payload.count;
        }

        state.status = 'succeeded';
      })
      .addCase(fetchPosts.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Unknown Error';
      })

      // #show
      .addCase(fetchPost.fulfilled, (state, action) => {
        const post = Object.values(action.payload.post)[0];
        state.entities[post.id] = post;
      })

      // #create
      .addCase(createPost.fulfilled, (state, action) => {
        const post = Object.values(action.payload.post)[0];
        state.entities[post.id] = post;
      })
      .addCase(createPost.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.newError = error || action.error.message || 'Unknown Error';
      })

      // #update
      .addCase(updatePost.fulfilled, (state, action) => {
        const post = Object.values(action.payload.post)[0];
        state.entities[post.id] = post;
      })
      .addCase(updatePost.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.editError = error || action.error.message || 'Unknown Error';
      })

      // #reports
      .addCase(fetchReports.fulfilled, (state, action) => {
        state.entities = { ...state.entities, ...action.payload.posts };
      })
      .addCase(fetchReport.fulfilled, (state, action) => {
        state.entities = { ...state.entities, ...action.payload.post };
      })

      // #resetRand
      .addCase(resetRand.pending, (state, _action) => {
        state.status = 'loading'
        state.ids = [];
        state.entities = {};
        state.count = 0;
      })
      .addCase(resetRand.fulfilled, (state, action) => {
        state.status = 'succeeded';
      })
      .addCase(resetRand.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Unknown Error';
      })
  }
});

const reducer = slice.reducer;
export default reducer;

export const {
  setEditId,
  setNewParams,
  setNewError,
  setEditError
} = slice.actions;

export const selectSilent = (state: RootState) => state.community.posts.silent;
export const selectSilentIds = createDraftSafeSelector(
  [selectSilent, (state, silent) => silent],
  (silent, silentId) => silent ? silent[silentId]?.ids : []
);
export const selectSilentCount = createDraftSafeSelector(
  [selectSilent, (_state, silent) => silent],
  (silent, silentId) => silent ? silent[silentId]?.count : []
);

export const {
  selectById: selectPostById,
  selectIds: selectPostIds,
  selectEntities: selectPostEntities,
  selectAll: selectAllPosts,
  selectTotal: selectTotalPosts
} = adaptor.getSelectors<RootState>(state => state.community.posts);
